import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { get, onSessionReset, sessionEpoch } from './api.ts';
import { stored } from './storage.ts';

/**
 * Tiny query cache: one entry per API path. `invalidate(prefix)` refetches every
 * entry whose path starts with one of the prefixes (stale data stays visible meanwhile).
 * Entries start from the copy saved on the device and are refetched on first use.
 */
interface Entry { data?: unknown; error?: Error; loading: boolean; fetched: boolean; promise?: Promise<void>; subs: Set<() => void>; snapshot: Snapshot<unknown> }
export interface Snapshot<T> { data: T | undefined; error: Error | undefined; loading: boolean }

const cache = new Map<string, Entry>();

function entry(key: string): Entry {
  let e = cache.get(key);
  if (!e) {
    const data = stored.read(key);
    e = { data, loading: false, fetched: false, subs: new Set(), snapshot: { data, error: undefined, loading: false } };
    cache.set(key, e);
  }
  return e;
}

function publish(e: Entry) {
  e.snapshot = { data: e.data, error: e.error, loading: e.loading };
  e.subs.forEach((s) => s());
}

function load(key: string): Promise<void> {
  const e = entry(key);
  if (e.promise) return e.promise;
  e.loading = true;
  e.fetched = true;
  publish(e);
  const started = sessionEpoch();
  e.promise = get(key)
    .then((d) => { if (started !== sessionEpoch()) return; e.data = d; e.error = undefined; stored.write(key, d); })
    .catch((err: Error) => { e.error = err; })
    .finally(() => { e.loading = false; e.promise = undefined; publish(e); });
  return e.promise;
}

export function useQuery<T>(key: string | null): Snapshot<T> & { reload: () => Promise<void> } {
  const subscribe = useCallback((cb: () => void) => {
    if (!key) return () => {};
    const e = entry(key);
    e.subs.add(cb);
    return () => { e.subs.delete(cb); };
  }, [key]);
  const snap = useSyncExternalStore(subscribe, () => (key ? entry(key).snapshot : EMPTY)) as Snapshot<T>;
  useEffect(() => {
    if (!key) return;
    const e = entry(key);
    if (!e.fetched && !e.promise) void load(key);
  }, [key]);
  const reload = useCallback(() => (key ? load(key) : Promise.resolve()), [key]);
  return { ...snap, loading: snap.loading || (!!key && snap.data === undefined && !snap.error), reload };
}
const EMPTY: Snapshot<unknown> = { data: undefined, error: undefined, loading: false };

/** warm the cache for a view that is likely to be opened next (the neighbouring period) */
export function prefetch(key: string) {
  const e = entry(key);
  if (e.fetched || e.promise) return;
  // a failed warm-up must not count as fetched: opening the view tries again
  void load(key).then(() => { if (e.error && !e.subs.size) e.fetched = false; });
}

export function invalidate(...prefixes: string[]) {
  for (const [key, e] of cache) {
    if (!prefixes.length || prefixes.some((p) => key.startsWith(p))) {
      if (e.subs.size) void load(key);
      else { cache.delete(key); }
    }
  }
}

/** pull to refresh: refetch everything on screen and wait for it; entries nobody shows are dropped */
export function refreshAll(): Promise<void> {
  const pending: Promise<void>[] = [];
  for (const [key, e] of cache) {
    if (e.subs.size) pending.push(load(key));
    else cache.delete(key);
  }
  return Promise.all(pending).then(() => undefined);
}

/** after any money-moving mutation most views are stale ('/shared': who still owes a part of a shared operation) */
export const invalidateMoney = () => invalidate('/dashboard', '/wallets', '/transactions', '/debts', '/goals', '/periods', '/reports', '/contacts', '/rates', '/finplan', '/notes', '/shared');

export function clearCache() {
  cache.clear();
  stored.clear();
}

onSessionReset(clearCache);
